import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaCheck, FaTimes, FaEdit, FaSearch, FaCalendar, FaClock, FaEye, FaUser, FaMoneyBillWave, FaMapMarkerAlt, FaDownload, FaFilter, FaPercent, FaCalculator, FaCoins, FaVideo, FaTicketAlt, FaTag } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Helper to format date as dd/mm/yy
function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = String(d.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
}


const PendingPost = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAd, setSelectedAd] = useState(null);
  const [modal, setModal] = useState({ open: false, action: null, adId: null });
  const [rejectReason, setRejectReason] = useState("");
  const navigate = useNavigate();
  const [refetchTrigger, setRefetchTrigger] = useState(false);
  const [showApproveSpinner, setShowApproveSpinner] = useState(false);
  const [showRejectSpinner, setShowRejectSpinner] = useState(false);
  const [editableBaseView, setEditableBaseView] = useState("");
  const [editableCommission, setEditableCommission] = useState("");
  const [isEditingControls, setIsEditingControls] = useState(false);
  const [showUpdateSpinner, setShowUpdateSpinner] = useState(false);

  useEffect(() => {
    const fetchAds = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/get/all/ads`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        console.log("Fetched  all Ads Data:", response.data);
        setAds(response.data || []);
      } catch (err) {
        setError("Failed to fetch ads");
      } finally {
        setLoading(false);
      }
    };
    fetchAds();
  }, [refetchTrigger]);

  // Search/filter states
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 50;
  const [todayOnly, setTodayOnly] = useState(false);

  // Set startDate and endDate to today when todayOnly is checked
  useEffect(() => {
    if (todayOnly) {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const todayStr = `${yyyy}-${mm}-${dd}`;
      setStartDate(todayStr);
      setEndDate(todayStr);
    }
  }, [todayOnly]);


  // Optionally set to end of day if endOfDay=true
  const toDate = (dateStr, endOfDay = false) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) {
      return null;
    }
    if (endOfDay) {
      d.setHours(23, 59, 59, 999);
    } else {
      d.setHours(0, 0, 0, 0);
    }
    return d;
  };


  const filteredAds = ads.filter((ad) => {
    // Only show pending ads - explicitly check status
    const adStatus = ad.status?.toLowerCase()?.trim();
    if (adStatus !== "pending") {
      return false;
    }


    // Convert ad dates to Date objects
    const adStart = toDate(ad.startDate); // start of ad
    const adEnd = toDate(ad.endDate, true); // end of ad (end of day)

    // Convert filter dates to Date objects
    // filterStart: start of day, filterEnd: end of day
    const filterStart = toDate(startDate); // 00:00:00.000
    const filterEnd = toDate(endDate, true); // 23:59:59.999


    // Skip ads with invalid dates if filters are active
    if ((todayOnly || filterStart || filterEnd) && (!adStart || !adEnd)) {

      return false;
    }

    if (todayOnly) {
      const today = toDate(new Date());
      // Ad must include today's date
      if (!(adStart <= today && adEnd >= today)) {

        return false;
      }
    } else if (filterStart && filterEnd) {

      // Show only ads where adStart and adEnd exactly match the selected dates
      if (!(adStart && adEnd && filterStart && filterEnd && adStart.getTime() === filterStart.getTime() && adEnd.getTime() === filterEnd.getTime())) {
        return false;
      }
    } else if (filterStart) {
      // Ad must end on or after the filter start date
      if (!adEnd || adEnd < filterStart) {
        return false;
      }
    } else if (filterEnd) {
      // Ad must start on or before the filter end date
      if (!adStart || adStart > filterEnd) {
        return false;
      }
    }

    // 🔎 Search filter
    if (searchText) {
      const s = searchText.toLowerCase();
      const idMatch = ad._id && ad._id.toLowerCase().includes(s);
      const stateMatch = ad.pfState && ad.pfState.toLowerCase().includes(s);
      const cityMatch =
        (ad.pfCities && ad.pfCities.some(city => city.toLowerCase().includes(s)));

      if (!idMatch && !stateMatch && !cityMatch) return false;
    }

    return true;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredAds.length / rowsPerPage);
  const paginatedAds = filteredAds.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle update both base view and commission
  const handleUpdateControls = async () => {
    const baseView = parseFloat(editableBaseView);
    const commission = parseFloat(editableCommission);

    if (!selectedAd) {
      toast.error("No advertisement selected");
      return;
    }

    if (editableBaseView !== "" && (isNaN(baseView) || baseView < 0)) {
      toast.error("Please enter a valid base view count");
      return;
    }

    if (editableCommission !== "" && (isNaN(commission) || commission < 0 || commission > 100)) {
      toast.error("Please enter a valid commission percentage (0-100)");
      return;
    }

    setShowUpdateSpinner(true);
    try {
      const updateData = {};
      if (editableBaseView !== "") updateData.baseView = baseView;
      if (editableCommission !== "") updateData.commissionPercentage = commission;

      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/update/controls/${selectedAd._id}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        toast.success("Controls updated successfully");
        // Update the selected ad with new data
        setSelectedAd(prev => ({
          ...prev,
          baseView: response.data.data.baseView || prev.baseView,
          requiredReporter: response.data.data.requiredReporter || prev.requiredReporter,
          adminCommission: response.data.data.adminCommission || prev.adminCommission,
          finalReporterPrice: response.data.data.finalReporterPrice || prev.finalReporterPrice,
          totalCost: response.data.data.totalCost || prev.totalCost
        }));
        setIsEditingControls(false);
        // Refresh the ads list
        setRefetchTrigger(prev => !prev);
      } else {
        toast.error(response.data.message || "Failed to update controls");
      }
    } catch (error) {
      console.error("Error updating controls:", error);
      toast.error("Failed to update controls");
    } finally {
      setShowUpdateSpinner(false);
    }
  };

  // Initialize editable values when modal opens
  const handleViewDetails = (ad) => {
    setSelectedAd(ad);
    setEditableBaseView(ad.baseView || 1000);
    // Calculate current commission percentage correctly
    const currentCommission = ad.totalCost > 0 ? ((ad.adminCommission / ad.totalCost) * 100) : 0;
    setEditableCommission(currentCommission.toFixed(1));
    setIsEditingControls(false);
  };


  // Handle approve advertisement
  const handleApprove = async (id) => {
    setShowApproveSpinner(true); // Show spinner immediately
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/advertisements/approve/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Approve ad Response:", response.data);
      // Check for success in both response.data and response
      if (response.data && response.data.success) {
        toast.success("Advertisement approved successfully");
        setTimeout(() => {
          setShowApproveSpinner(false);
          window.location.reload();
        }, 1500);
      } else {
        setShowApproveSpinner(false);
        toast.error(
          (response.data && response.data.message) ||
          "Failed to approve advertisement"
        );
      }
    } catch (error) {
      setShowApproveSpinner(false);
      console.error("Error approving advertisement:", error);

      // Display detailed error message from backend
      let errorMessage = "Failed to approve advertisement";
      if (error.response && error.response.data) {
        if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.details) {
          errorMessage = error.response.data.details;
        } else if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
          errorMessage = error.response.data.errors.join("; ");
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, { autoClose: 5000 });
    }
  };

  // Handle reject advertisement
  const handleReject = async (id, reason) => {
    console.log("Rejecting advertisement:", id, "Reason:", reason);
    setShowRejectSpinner(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/advertisements/reject/${id}`,
        { adminRejectNote: reason },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Reject Response:", response.data);
      if (response.data.success) {
        toast.success(
          response.data.message || "Advertisement rejected successfully"
        );
        setTimeout(() => {
          setShowRejectSpinner(false);
          window.location.reload();
        }, 1500);
      } else {
        setShowRejectSpinner(false);
        toast.error(response.data.message || "Failed to reject advertisement");
      }
    } catch (error) {
      setShowRejectSpinner(false);
      console.error("Error rejecting advertisement:", error);
      toast.error("Failed to reject advertisement");
    }
  };

  const handleModifyAd = (id) => {
    navigate("/admin/advertisement/modify", { state: { adId: id } });
  };

  // Modal confirm handler
  const handleModalConfirm = () => {
    if (modal.action === "approve") {
      handleApprove(modal.adId);
    } else if (modal.action === "reject") {
      // Check if reject reason is provided
      if (!rejectReason.trim()) {
        toast.error("Please enter a reason for rejection before proceeding.");
        return; // Don't close modal or proceed
      }
      handleReject(modal.adId, rejectReason);
    }
    setModal({ open: false, action: null, adId: null });
    setRejectReason("");
  };

  // Modal cancel handler
  const handleModalCancel = () => {
    setModal({ open: false, action: null, adId: null });
    setRejectReason("");
  };

  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl border border-yellow-200">
        {/* Custom Tailwind Spinner */}
        <div className="relative mb-4 w-16 h-16 grid place-items-center">
          {/* Outer ring */}
          <span className="absolute inset-0 rounded-full border-4 border-transparent border-r-yellow-500 animate-spin" style={{ borderWidth: "4px" }} />
          {/* Middle ring */}
          <span className="absolute inset-0 m-1 rounded-full border-4 border-transparent border-r-yellow-500 animate-spin-slow" style={{ borderWidth: "4px", animationDuration: "2s" }} />
          {/* Inner ring */}
          <span className="absolute inset-0 m-2 rounded-full border-4 border-transparent border-r-yellow-500 animate-spin-slower" style={{ borderWidth: "4px", animationDuration: "3s" }} />
        </div>
        <span className="text-lg font-semibold text-yellow-700 tracking-wide">
          Loading Pending Ads...
        </span>
        <span className="text-sm text-gray-500 mt-2">
          Please wait while we fetch the data.
        </span>
      </div>
    </div>
  );

  if (error) return (
    <div className="flex justify-center items-center h-64">
      <div className="bg-red-100 border border-red-400 text-red-700 px-8 py-6 rounded-2xl shadow-lg">
        <div className="flex items-center gap-3">
          <FaTimes className="w-6 h-6 text-red-500" />
          <div>
            <strong className="font-bold text-lg">Error!</strong>
            <span className="block mt-1">{error}</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Header Section */}
      <div className="bg-gradient-to-r from-yellow-600 via-amber-600 to-orange-700 rounded-xl shadow-lg p-4 mb-4 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <h1 className="text-xl lg:text-2xl font-bold mb-1 flex items-center gap-2">
                <FaClock className="text-yellow-300 w-5 h-5" />
                Pending Advertisements
              </h1>
              <p className="text-yellow-100 text-sm">
                Review and manage advertisements awaiting approval
              </p>
              <div className="mt-2 flex items-center gap-2">
                <div className="bg-white bg-opacity-20 rounded-lg px-3 py-1">
                  <span className="text-xs text-yellow-100">Total Pending</span>
                  <div className="text-lg font-bold text-white">{filteredAds.length}</div>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg px-3 py-1">
                  <span className="text-xs text-yellow-100">This Page</span>
                  <div className="text-lg font-bold text-white">{Math.min(rowsPerPage, filteredAds.length)}</div>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex flex-wrap gap-2">
              <button
                className="bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded-lg font-semibold text-xs shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-1"
                onClick={() => navigate("/admin/approvedposts")}
              >
                <FaCheck className="w-3 h-3" />
                Approved
              </button>
              <button
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1.5 rounded-lg font-semibold text-xs shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-1"
                onClick={() => navigate("/admin/acceptedbyreporterposts")}
              >
                <FaUser className="w-3 h-3" />
                Accepted
              </button>
              <button
                className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg font-semibold text-xs shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-1"
                onClick={() => navigate("/admin/rejectedposts")}
              >
                <FaTimes className="w-3 h-3" />
                Rejected
              </button>
              <button
                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded-lg font-semibold text-xs shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-1"
                onClick={() => navigate("/admin/runningposts")}
              >
                <FaEye className="w-3 h-3" />
                Running
              </button>
              <button
                className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-1.5 rounded-lg font-semibold text-xs shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-1"
                onClick={() => navigate("/admin/completedposts")}
              >
                <FaCheck className="w-3 h-3" />
                Completed
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="bg-white rounded-xl shadow-md p-3 mb-4 border border-gray-100">
        <div className="flex flex-col lg:flex-row lg:items-center gap-2">
          <div className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
              <input
                type="text"
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-300 bg-gray-50 focus:bg-white"
                placeholder="Search by Ad ID, State, or Cities..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="bg-yellow-50 rounded-lg px-2 py-1">
              <span className="text-xs text-yellow-600 font-medium">Page {currentPage}/{totalPages}</span>
            </div>
            <div className="bg-blue-50 rounded-lg px-2 py-1">
              <span className="text-xs text-blue-600 font-medium">{filteredAds.length} Results</span>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-2 rounded-lg font-semibold text-xs shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-1"
            >
              <FaDownload className="w-3 h-3" />
              Refresh
            </button>
          </div>
        </div>

        {/* Date Filters */}
        <div className="mt-2 flex flex-col sm:flex-row gap-2">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex items-center gap-1">
              <FaCalendar className="text-yellow-600 w-3 h-3" />
              <span className="text-xs font-medium text-gray-700">Start:</span>
              <input
                type="date"
                className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                value={startDate}
                onChange={e => {
                  setStartDate(e.target.value);
                  setTodayOnly(false);
                }}
                max={endDate || undefined}
              />
            </div>
            <div className="flex items-center gap-1">
              <FaCalendar className="text-yellow-600 w-3 h-3" />
              <span className="text-xs font-medium text-gray-700">End:</span>
              <input
                type="date"
                className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                value={endDate}
                onChange={e => {
                  setEndDate(e.target.value);
                  setTodayOnly(false);
                }}
                min={startDate || undefined}
              />
            </div>
          </div>
          <label className="flex items-center gap-1 text-yellow-600 font-medium text-xs cursor-pointer bg-yellow-50 px-2 py-1 rounded-lg hover:bg-yellow-100 transition-colors">
            <input
              type="checkbox"
              className="accent-yellow-500 w-3 h-3"
              checked={todayOnly}
              onChange={e => {
                setTodayOnly(e.target.checked);
                if (e.target.checked) {
                  setStartDate("");
                  setEndDate("");
                }
              }}
            />
            <FaClock className="w-3 h-3" />
            Today Only
          </label>
        </div>
      </div>
      {/* Main Content */}
      {filteredAds.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-yellow-100 to-amber-100 rounded-full flex items-center justify-center">
            <FaClock className="w-12 h-12 text-yellow-500" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-3">
            No Pending Advertisements
          </h3>
          <p className="text-gray-500 text-lg mb-6">
            You don't have any pending advertisements at the moment.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
          >
            Refresh
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gradient-to-r from-yellow-50 to-amber-50">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                    <FaCalendar className="inline w-3 h-3 mr-1" />
                    Start
                  </th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                    <FaCalendar className="inline w-3 h-3 mr-1" />
                    End
                  </th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                    <FaUser className="inline w-3 h-3 mr-1" />
                    Ad ID
                  </th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                    <FaMoneyBillWave className="inline w-3 h-3 mr-1" />
                    Budget
                  </th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                    <FaCalculator className="inline w-3 h-3 mr-1" />
                    Financial
                  </th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                    <FaFilter className="inline w-3 h-3 mr-1" />
                    Category
                  </th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                    <FaTag className="inline w-3 h-3 mr-1" />
                    Platforms
                  </th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                    <FaUser className="inline w-3 h-3 mr-1" />
                    User Type
                  </th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                    <FaMapMarkerAlt className="inline w-3 h-3 mr-1" />
                    State
                  </th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                    <FaMapMarkerAlt className="inline w-3 h-3 mr-1" />
                    Cities
                  </th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                    <FaClock className="inline w-3 h-3 mr-1" />
                    Created
                  </th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                    <FaClock className="inline w-3 h-3 mr-1" />
                    Status
                  </th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                    <FaEye className="inline w-3 h-3 mr-1" />
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedAds.map((ad) => (
                  <tr key={ad._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-2 text-xs text-gray-900 font-medium">
                      {formatDate(ad.startDate)}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-900 font-medium">
                      {formatDate(ad.endDate)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="bg-yellow-100 text-yellow-800 rounded-lg px-2 py-0.5 font-mono text-[10px] truncate max-w-[120px]">
                        {ad._id}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-900 font-semibold">
                      ₹{ad.totalCost}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-blue-600 font-medium">
                            Comm: ₹{ad.adminCommission ? Number(ad.adminCommission).toFixed(0) : "0"}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-green-600 font-medium">
                            Rep: ₹{ad.finalReporterPrice ? Number(ad.finalReporterPrice).toFixed(0) : "0"}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-purple-600 font-medium">
                            {ad.requiredReporter || 0} reps
                          </span>
                        </div>
                        {ad.couponCode && (
                          <div className="flex items-center gap-1">
                            <FaTicketAlt className="w-2 h-2 text-orange-500" />
                            <span className="text-[10px] text-orange-600 font-medium truncate max-w-[80px]">
                              {ad.couponCode}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-900">
                      {ad.adType}
                    </td>
                    <td className="px-3 py-2">
                      {ad.platforms && ad.platforms.length > 0 ? (
                        <div className="flex flex-wrap gap-0.5">
                          {ad.platforms.slice(0, 2).map((platform, idx) => (
                            <span key={idx} className="bg-purple-100 text-purple-800 text-[10px] px-1.5 py-0.5 rounded-full">
                              {platform}
                            </span>
                          ))}
                          {ad.platforms.length > 2 && (
                            <span className="bg-gray-100 text-gray-600 text-[10px] px-1.5 py-0.5 rounded-full">
                              +{ad.platforms.length - 2}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-[10px]">N/A</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${ad.userType === 'influencer'
                        ? 'bg-purple-100 text-purple-800'
                        : ad.userType === 'both'
                          ? 'bg-gradient-to-r from-purple-100 to-blue-100 text-gray-800'
                          : 'bg-blue-100 text-blue-800'
                        }`}>
                        {ad.userType === 'influencer' ? '🎯 Influencer' : ad.userType === 'both' ? '🎯📰 Both' : '📰 Reporter'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-900">
                      {ad.pfState || "N/A"}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-900 max-w-[120px] truncate">
                      {ad.pfCities && ad.pfCities.length > 0 ? ad.pfCities.join(", ") : "N/A"}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      <div>
                        <div className="font-medium text-[10px]">{new Date(ad.createdAt).toLocaleDateString('en-IN', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}</div>
                        <div className="text-gray-400 text-[9px]">{new Date(ad.createdAt).toLocaleTimeString('en-IN', {
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true,
                        })}</div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${ad.status === "approved"
                        ? "bg-green-100 text-green-800"
                        : ad.status === "rejected"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                        }`}>
                        {ad.status === "pending" ? "Review" : ad.status}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded-lg font-semibold text-[10px] shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-1"
                        onClick={() => handleViewDetails(ad)}
                      >
                        <FaEye className="w-3 h-3" />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-8">
          <nav className="inline-flex items-center space-x-2 bg-white rounded-2xl shadow-lg p-2 border border-gray-100">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${currentPage === 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-yellow-600 hover:bg-yellow-50 hover:text-yellow-700'
                }`}
            >
              Previous
            </button>

            {[...Array(totalPages)].map((_, idx) => (
              <button
                key={idx + 1}
                onClick={() => handlePageChange(idx + 1)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${currentPage === idx + 1
                  ? 'bg-yellow-500 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                  }`}
                aria-current={currentPage === idx + 1 ? 'page' : undefined}
              >
                {idx + 1}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${currentPage === totalPages
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-yellow-600 hover:bg-yellow-50 hover:text-yellow-700'
                }`}
            >
              Next
            </button>
          </nav>
        </div>
      )}


      {/* Modern Advertisement Details Modal */}
      {selectedAd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-y-auto relative border border-gray-200">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-yellow-600 via-amber-600 to-orange-700 text-white p-6 rounded-t-2xl relative">
              <button
                className="absolute top-4 right-4 text-white hover:text-gray-200 text-2xl font-bold bg-white bg-opacity-20 rounded-full w-8 h-8 flex items-center justify-center transition-colors"
                onClick={() => setSelectedAd(null)}
                title="Close"
              >
                &times;
              </button>
              <div className="flex items-center gap-3">
                <div className="bg-white bg-opacity-20 rounded-lg p-2">
                  <FaEye className="w-6 h-6 text-yellow-300" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">
                    Advertisement Details
                  </h3>
                  <p className="text-yellow-100 text-sm">
                    Complete information about this advertisement
                  </p>
                </div>
              </div>
            </div>

            <div className="p-8">

              {/* Information Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {/* Basic Information Card */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl border border-blue-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-blue-500 text-white rounded-lg p-2">
                      <FaUser className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">Basic Information</h5>
                      <p className="text-sm text-gray-600">Advertisement basic details</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Ad ID</span>
                      <span className="font-mono text-sm text-gray-900 break-all">{selectedAd._id}</span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Category</span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                        {selectedAd.adType || "N/A"}
                      </span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Media Type</span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        {selectedAd.mediaType}
                      </span>
                    </div>
                    {selectedAd.platforms && selectedAd.platforms.length > 0 && (
                      <div className="bg-white rounded-lg p-3 border border-blue-200">
                        <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Platforms</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedAd.platforms.map((platform, idx) => (
                            <span key={idx} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              {platform}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedAd.channelType && selectedAd.channelType.length > 0 && (
                      <div className="bg-white rounded-lg p-3 border border-blue-200">
                        <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Channels</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedAd.channelType.map((channel, idx) => (
                            <span key={idx} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              {channel}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedAd.couponCode && (
                      <div className="bg-white rounded-lg p-3 border border-blue-200">
                        <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Coupon Code</span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800 gap-2">
                          <FaTicketAlt className="w-3 h-3" />
                          {selectedAd.couponCode}
                        </span>
                      </div>
                    )}
                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Status</span>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${selectedAd.status === "approved"
                        ? "bg-green-100 text-green-800"
                        : selectedAd.status === "rejected"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                        }`}>
                        {selectedAd.status === "pending" ? "Under Review" : selectedAd.status}
                      </span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Target User Type</span>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${selectedAd.userType === 'influencer'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-blue-100 text-blue-800'
                        }`}>
                        {selectedAd.userType === 'influencer' ? '🎯 Influencer' : '📰 Reporter'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Campaign Details Card */}
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl border border-green-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-green-500 text-white rounded-lg p-2">
                      <FaMoneyBillWave className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">Campaign Details</h5>
                      <p className="text-sm text-gray-600">Budget and campaign specifications</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Budget</span>
                      <span className="text-2xl font-bold text-green-600">₹{selectedAd.totalCost}</span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Ad Length</span>
                      <span className="text-lg font-bold text-green-600">{selectedAd.adLength}</span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Required Views</span>
                      <span className="text-lg font-bold text-green-600">{selectedAd.requiredViews?.toLocaleString() || "N/A"}</span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Required Reporters</span>
                      <span className="text-lg font-bold text-green-600">{selectedAd.requiredReporter || "N/A"}</span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Created At</span>
                      <span className="text-sm font-medium text-gray-900">
                        {new Date(selectedAd.createdAt).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Location Information Card */}
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl border border-purple-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-purple-500 text-white rounded-lg p-2">
                      <FaMapMarkerAlt className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">Location Information</h5>
                      <p className="text-sm text-gray-600">Geographic targeting details</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-3 border border-purple-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">State</span>
                      <span className="font-medium text-gray-900">{selectedAd.adState}</span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-purple-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">City</span>
                      <span className="font-medium text-gray-900">{selectedAd.adCity}</span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-purple-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Pin Code</span>
                      <span className="font-medium text-gray-900">{selectedAd.pincode || "N/A"}</span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-purple-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Preference State</span>
                      <span className="font-medium text-gray-900">{selectedAd.pfState || "N/A"}</span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-purple-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Preference Cities</span>
                      <span className="font-medium text-gray-900">
                        {Array.isArray(selectedAd.pfCities)
                          ? selectedAd.pfCities.join(", ")
                          : selectedAd.pfCities || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Date Information Card */}
                <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-6 rounded-2xl border border-indigo-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-indigo-500 text-white rounded-lg p-2">
                      <FaCalendar className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">Date Information</h5>
                      <p className="text-sm text-gray-600">Campaign duration and timing</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-3 border border-indigo-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Start Date</span>
                      <span className="font-medium text-gray-900">{selectedAd.startDate}</span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-indigo-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">End Date</span>
                      <span className="font-medium text-gray-900">{selectedAd.endDate}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Financial Details Section */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-100 p-6 rounded-2xl border border-emerald-200 mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-emerald-500 text-white rounded-lg p-2">
                    <FaCalculator className="w-4 h-4" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-800">Financial Breakdown</h5>
                    <p className="text-sm text-gray-600">Commission, rewards, and savings analysis</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Total Budget */}
                  <div className="bg-white rounded-xl p-4 border border-emerald-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <FaMoneyBillWave className="w-4 h-4 text-emerald-600" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Total Budget</span>
                    </div>
                    <div className="text-2xl font-bold text-emerald-600">₹{selectedAd.totalCost}</div>
                    <div className="text-xs text-gray-500">Advertiser Payment</div>
                  </div>

                  {/* Platform Commission */}
                  <div className="bg-white rounded-xl p-4 border border-blue-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <FaPercent className="w-4 h-4 text-blue-600" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Platform Commission</span>
                    </div>
                    <div className="text-2xl font-bold text-blue-600">
                      ₹{selectedAd.adminCommission ? Number(selectedAd.adminCommission).toFixed(2) : "0.00"}
                    </div>
                    <div className="text-xs text-gray-500">
                      {selectedAd.totalCost > 0 ? `${((selectedAd.adminCommission / selectedAd.totalCost) * 100).toFixed(1)}%` : "0%"} of total
                    </div>
                  </div>

                  {/* Reporter Rewards */}
                  <div className="bg-white rounded-xl p-4 border border-green-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <FaCoins className="w-4 h-4 text-green-600" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Reporter Rewards</span>
                    </div>
                    <div className="text-2xl font-bold text-green-600">
                      ₹{selectedAd.finalReporterPrice ? Number(selectedAd.finalReporterPrice).toFixed(2) : "0.00"}
                    </div>
                    <div className="text-xs text-gray-500">
                      Per reporter ({selectedAd.requiredReporter || 0} reporters)
                    </div>
                  </div>

                  {/* Total Reporter Budget */}
                  <div className="bg-white rounded-xl p-4 border border-purple-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <FaUser className="w-4 h-4 text-purple-600" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Total Reporter Budget</span>
                    </div>
                    <div className="text-2xl font-bold text-purple-600">
                      ₹{selectedAd.finalReporterPrice && selectedAd.requiredReporter
                        ? (Number(selectedAd.finalReporterPrice) * Number(selectedAd.requiredReporter)).toFixed(2)
                        : "0.00"}
                    </div>
                    <div className="text-xs text-gray-500">
                      {selectedAd.requiredReporter || 0} reporters × ₹{selectedAd.finalReporterPrice || "0.00"}
                    </div>
                  </div>
                </div>

                {/* Coupon Code Display */}
                {selectedAd.couponCode && (
                  <div className="mt-6 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-200">
                    <div className="flex items-center gap-3">
                      <div className="bg-orange-500 text-white rounded-lg p-2">
                        <FaTicketAlt className="w-4 h-4" />
                      </div>
                      <div>
                        <h6 className="font-semibold text-gray-800">Coupon Code Applied</h6>
                        <p className="text-sm text-gray-600">This advertisement used a coupon code</p>
                      </div>
                      <div className="ml-auto">
                        <span className="inline-flex items-center px-4 py-2 rounded-lg text-lg font-bold bg-orange-500 text-white gap-2">
                          <FaTicketAlt className="w-4 h-4" />
                          {selectedAd.couponCode}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Detailed Breakdown */}
                <div className="mt-6 bg-white rounded-xl p-4 border border-gray-200">
                  <h6 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <FaCalculator className="w-4 h-4 text-gray-600" />
                    Detailed Financial Analysis
                  </h6>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Advertisement Cost:</span>
                        <span className="font-medium">₹{selectedAd.totalCost}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Platform Commission ({selectedAd.totalCost > 0 ? ((selectedAd.adminCommission / selectedAd.totalCost) * 100).toFixed(1) : "0.0"}%):</span>
                        <span className="font-medium text-blue-600">₹{selectedAd.adminCommission ? Number(selectedAd.adminCommission).toFixed(2) : "0.00"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Reporter Budget:</span>
                        <span className="font-medium text-green-600">
                          ₹{selectedAd.finalReporterPrice && selectedAd.requiredReporter
                            ? (Number(selectedAd.finalReporterPrice) * Number(selectedAd.requiredReporter)).toFixed(2)
                            : "0.00"}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Required Views:</span>
                        <span className="font-medium">{selectedAd.requiredViews?.toLocaleString() || "0"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Base View per Reporter:</span>
                        <span className="font-medium">{selectedAd.baseView || "N/A"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Views per Reporter:</span>
                        <span className="font-medium text-purple-600">
                          {selectedAd.baseView ? Math.ceil((selectedAd.requiredViews || 0) / selectedAd.baseView) : "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Savings Analysis */}
                <div className="mt-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 border border-yellow-200">
                  <h6 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                    <FaCoins className="w-4 h-4" />
                    Cost Efficiency Analysis
                  </h6>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="text-center">
                      <div className="text-lg font-bold text-yellow-700">
                        ₹{selectedAd.finalReporterPrice ? (Number(selectedAd.finalReporterPrice) / (selectedAd.baseView || 1)).toFixed(4) : "0.0000"}
                      </div>
                      <div className="text-xs text-yellow-600">Cost per View</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-orange-700">
                        {selectedAd.requiredReporter || 0}
                      </div>
                      <div className="text-xs text-orange-600">Reporters Needed</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-700">
                        {selectedAd.totalCost > 0 ? (100 - ((selectedAd.adminCommission / selectedAd.totalCost) * 100)).toFixed(1) : "0"}%
                      </div>
                      <div className="text-xs text-green-600">Reporter Share</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Advertisement Overview Section */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-6 rounded-2xl border border-blue-200 mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-blue-500 text-white rounded-lg p-2">
                    <FaEye className="w-4 h-4" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-800">Advertisement Being Modified</h5>
                    <p className="text-sm text-gray-600">Complete details of the advertisement you are about to modify</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Advertisement ID */}
                  <div className="bg-white rounded-xl p-4 border border-blue-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <FaUser className="w-4 h-4 text-blue-600" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Ad ID</span>
                    </div>
                    <div className="text-sm font-mono text-blue-600 break-all">{selectedAd._id}</div>
                  </div>

                  {/* Ad Type */}
                  <div className="bg-white rounded-xl p-4 border border-blue-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <FaFilter className="w-4 h-4 text-blue-600" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Ad Type</span>
                    </div>
                    <div className="text-lg font-bold text-blue-600">{selectedAd.adType}</div>
                  </div>

                  {/* Target User Type */}
                  <div className="bg-white rounded-xl p-4 border border-blue-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <FaUser className="w-4 h-4 text-blue-600" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Target</span>
                    </div>
                    <div className="text-lg font-bold text-blue-600">
                      {selectedAd.userType === 'influencer' ? '🎯 Influencer' : '📰 Reporter'}
                    </div>
                  </div>

                  {/* Media Type */}
                  <div className="bg-white rounded-xl p-4 border border-blue-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <FaVideo className="w-4 h-4 text-blue-600" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Media</span>
                    </div>
                    <div className="text-lg font-bold text-blue-600 capitalize">{selectedAd.mediaType}</div>
                  </div>

                  {/* Total Budget */}
                  <div className="bg-white rounded-xl p-4 border border-green-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <FaMoneyBillWave className="w-4 h-4 text-green-600" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Total Budget</span>
                    </div>
                    <div className="text-xl font-bold text-green-600">₹{selectedAd.totalCost}</div>
                  </div>

                  {/* Required Views */}
                  <div className="bg-white rounded-xl p-4 border border-purple-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <FaEye className="w-4 h-4 text-purple-600" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Required Views</span>
                    </div>
                    <div className="text-xl font-bold text-purple-600">{selectedAd.requiredViews?.toLocaleString() || "N/A"}</div>
                  </div>

                  {/* Current Base View */}
                  <div className="bg-white rounded-xl p-4 border border-orange-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <FaCalculator className="w-4 h-4 text-orange-600" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Current Base View</span>
                    </div>
                    <div className="text-xl font-bold text-orange-600">{selectedAd.baseView || "N/A"}</div>
                  </div>

                  {/* Current Required Reporters */}
                  <div className="bg-white rounded-xl p-4 border border-indigo-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <FaUser className="w-4 h-4 text-indigo-600" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Current Reporters</span>
                    </div>
                    <div className="text-xl font-bold text-indigo-600">{selectedAd.requiredReporter || "N/A"}</div>
                  </div>
                </div>

                {/* Campaign Timeline */}
                <div className="mt-6 bg-white rounded-xl p-4 border border-blue-200">
                  <h6 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                    <FaCalendar className="w-4 h-4" />
                    Campaign Timeline
                  </h6>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-700">
                        {formatDate(selectedAd.startDate)}
                      </div>
                      <div className="text-xs text-blue-600">Start Date</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-700">
                        {formatDate(selectedAd.endDate)}
                      </div>
                      <div className="text-xs text-green-600">End Date</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-purple-700">
                        {selectedAd.adLength}
                      </div>
                      <div className="text-xs text-purple-600">Ad Length</div>
                    </div>
                  </div>
                </div>

                {/* Preference Targeting */}
                {(selectedAd.pfState || (selectedAd.pfCities && selectedAd.pfCities.length > 0)) && (
                  <div className="mt-6 bg-white rounded-xl p-4 border border-blue-200">
                    <h6 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                      <FaMapMarkerAlt className="w-4 h-4" />
                      Preference Targeting
                    </h6>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="text-center">
                        <div className="text-lg font-bold text-blue-700">
                          {selectedAd.pfState || "Not specified"}
                        </div>
                        <div className="text-xs text-blue-600">Preference State</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-700">
                          {selectedAd.pfCities && selectedAd.pfCities.length > 0 ? selectedAd.pfCities.join(", ") : "Not specified"}
                        </div>
                        <div className="text-xs text-green-600">Preference Cities</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Advertisement Controls Section */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-6 rounded-2xl border border-blue-200 mb-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-500 text-white rounded-lg p-2">
                      <FaCalculator className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">Advertisement Controls</h5>
                      <p className="text-sm text-gray-600">Modify base view and commission - system will auto-calculate all values</p>
                    </div>
                  </div>
                  {!isEditingControls && (
                    <button
                      onClick={() => setIsEditingControls(true)}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                    >
                      <FaEdit className="w-4 h-4" />
                      Edit Controls
                    </button>
                  )}
                </div>

                {isEditingControls ? (
                  <div className="bg-white rounded-xl p-6 border border-blue-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Base View Input */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Base View per Reporter
                        </label>
                        <input
                          type="text"
                          value={editableBaseView}
                          onChange={(e) => setEditableBaseView(e.target.value)}
                          className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg font-semibold"
                          placeholder="Enter base view"
                        />
                        <div className="text-xs text-gray-500 mt-1">Views each reporter needs to achieve</div>
                      </div>

                      {/* Commission Input */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Commission Percentage (%)
                        </label>
                        <input
                          type="text"
                          value={editableCommission}
                          onChange={(e) => setEditableCommission(e.target.value)}
                          className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg font-semibold"
                          placeholder="Enter commission %"
                        />
                        <div className="text-xs text-gray-500 mt-1">Percentage of total cost that goes to platform</div>
                      </div>
                    </div>

                    {/* Auto-calculation Display */}
                    <div className="mt-6 bg-blue-50 rounded-xl p-4 border border-blue-200">
                      <h6 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                        <FaCalculator className="w-4 h-4" />
                        Live Calculation Preview
                      </h6>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                        <div className="text-center">
                          <div className="text-lg font-bold text-blue-700">
                            {editableBaseView && selectedAd.requiredViews ? Math.ceil(selectedAd.requiredViews / parseFloat(editableBaseView)) : "N/A"}
                          </div>
                          <div className="text-xs text-blue-600">Required Reporters</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-green-700">
                            {editableCommission && selectedAd.totalCost ? `₹${((selectedAd.totalCost * parseFloat(editableCommission)) / 100).toFixed(2)}` : "N/A"}
                          </div>
                          <div className="text-xs text-green-600">Admin Commission</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-purple-700">
                            {editableCommission && selectedAd.totalCost ? `₹${(selectedAd.totalCost - ((selectedAd.totalCost * parseFloat(editableCommission)) / 100)).toFixed(2)}` : "N/A"}
                          </div>
                          <div className="text-xs text-purple-600">Reporter Reward</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-orange-700">
                            {editableCommission && selectedAd.totalCost && editableBaseView && selectedAd.requiredViews ?
                              `₹${((selectedAd.totalCost - ((selectedAd.totalCost * parseFloat(editableCommission)) / 100)) / Math.ceil(selectedAd.requiredViews / parseFloat(editableBaseView))).toFixed(2)}` : "N/A"}
                          </div>
                          <div className="text-xs text-orange-600">Per Reporter</div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-6 flex gap-3">
                      <button
                        onClick={handleUpdateControls}
                        disabled={showUpdateSpinner}
                        className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                      >
                        {showUpdateSpinner ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Updating...
                          </>
                        ) : (
                          <>
                            <FaCheck className="w-4 h-4" />
                            Update Controls
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setIsEditingControls(false);
                          setEditableBaseView(selectedAd.baseView || 1000);
                          const currentCommission = selectedAd.totalCost > 0 ? ((selectedAd.adminCommission / selectedAd.totalCost) * 100) : 0;
                          setEditableCommission(currentCommission.toFixed(1));
                        }}
                        className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                      >
                        <FaTimes className="w-4 h-4" />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl p-4 border border-blue-200">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-indigo-600">
                          {selectedAd.baseView || "N/A"}
                        </div>
                        <div className="text-sm text-gray-600">Base View per Reporter</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {selectedAd.requiredReporter || 0}
                        </div>
                        <div className="text-sm text-gray-600">Required Reporters</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {selectedAd.totalCost > 0 ? ((selectedAd.adminCommission / selectedAd.totalCost) * 100).toFixed(1) : "0.0"}%
                        </div>
                        <div className="text-sm text-gray-600">Commission</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">
                          ₹{selectedAd.adminCommission || "0.00"}
                        </div>
                        <div className="text-sm text-gray-600">Admin Commission</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-orange-600">
                          ₹{selectedAd.finalReporterPrice || "0.00"}
                        </div>
                        <div className="text-sm text-gray-600">Reporter Reward</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Original Details Section */}
              <div className="bg-gradient-to-br from-yellow-50 to-orange-100 p-6 rounded-2xl border border-yellow-200 mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-yellow-500 text-white rounded-lg p-2">
                    <FaCalculator className="w-4 h-4" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-800">Original Advertisement Details</h5>
                    <p className="text-sm text-gray-600">Original financial breakdown and commission details</p>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 border border-yellow-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        ₹{selectedAd.totalCost || "0.00"}
                      </div>
                      <div className="text-sm text-gray-600">Total Budget</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {selectedAd.totalCost > 0 ? ((selectedAd.adminCommission / selectedAd.totalCost) * 100).toFixed(1) : "0.0"}%
                      </div>
                      <div className="text-sm text-gray-600">Original Commission %</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        ₹{selectedAd.adminCommission || "0.00"}
                      </div>
                      <div className="text-sm text-gray-600">Original Admin Commission</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-orange-600">
                        ₹{selectedAd.finalReporterPrice || "0.00"}
                      </div>
                      <div className="text-sm text-gray-600">Original Reporter Reward</div>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-yellow-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div className="text-center">
                        <div className="text-lg font-bold text-indigo-600">
                          {selectedAd.requiredViews || 0}
                        </div>
                        <div className="text-xs text-gray-600">Total Required Views</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-600">
                          {selectedAd.baseView || "N/A"}
                        </div>
                        <div className="text-xs text-gray-600">Original Base View</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-purple-600">
                          {selectedAd.requiredReporter || 0}
                        </div>
                        <div className="text-xs text-gray-600">Original Required Reporters</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Media Section */}
              {(selectedAd.mediaType === "image" && selectedAd.imageUrl) || (selectedAd.mediaType === "video" && selectedAd.videoUrl) ? (
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-2xl border border-gray-200">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-gray-500 text-white rounded-lg p-2">
                      {selectedAd.mediaType === "image" ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">Advertisement Media</h5>
                      <p className="text-sm text-gray-600">{selectedAd.mediaType === "image" ? "Image content" : "Video content"}</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-center min-h-[300px] bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                      {selectedAd.mediaType === "image" && selectedAd.imageUrl && (
                        <img
                          src={selectedAd.imageUrl}
                          alt={selectedAd.title || "Advertisement"}
                          className="max-w-full h-auto max-h-[280px] rounded-lg shadow-sm border border-gray-200 object-contain"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/fallback-image.jpg";
                          }}
                        />
                      )}
                      {selectedAd.mediaType === "video" && selectedAd.videoUrl && (
                        <video
                          src={selectedAd.videoUrl}
                          controls
                          className="max-w-full h-auto max-h-[280px] rounded-lg shadow-sm border border-gray-200"
                        />
                      )}
                    </div>
                    <div className="mt-4 flex justify-end">
                      <a
                        href={selectedAd.mediaType === "image" ? selectedAd.imageUrl : selectedAd.videoUrl}
                        download
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium transition-colors shadow-sm hover:shadow-md"
                      >
                        <FaDownload className="w-3 h-3" />
                        Download {selectedAd.mediaType === "image" ? "Image" : "Video"}
                      </a>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Description Section */}
              {selectedAd.mediaDescription && (
                <div className="mt-6 bg-gradient-to-br from-amber-50 to-amber-100 p-6 rounded-2xl border border-amber-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-amber-500 text-white rounded-lg p-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">Description</h5>
                      <p className="text-sm text-gray-600">Advertisement content description</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-amber-200">
                    <p className="text-gray-700">{selectedAd.mediaDescription}</p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {selectedAd.status === "pending" && (
                <div className="mt-8 flex justify-center gap-4">
                  <button
                    onClick={() => {
                      setModal({
                        open: true,
                        action: "approve",
                        adId: selectedAd._id,
                      });
                      setSelectedAd(null);
                    }}
                    className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold transition-colors shadow-lg hover:shadow-xl flex items-center gap-2"
                  >
                    <FaCheck className="w-4 h-4" />
                    Approve
                  </button>
                  <button
                    onClick={() => {
                      setModal({
                        open: true,
                        action: "reject",
                        adId: selectedAd._id,
                      });
                      setSelectedAd(null);
                    }}
                    className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-semibold transition-colors shadow-lg hover:shadow-xl flex items-center gap-2"
                  >
                    <FaTimes className="w-4 h-4" />
                    Reject
                  </button>
                  <button
                    onClick={() => {
                      handleModifyAd(selectedAd._id);
                      setSelectedAd(null);
                    }}
                    className="px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl font-semibold transition-colors shadow-lg hover:shadow-xl flex items-center gap-2"
                  >
                    <FaEdit className="w-4 h-4" />
                    Modify
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Modal for confirm action */}
      {modal.open && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 transition-all">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-[350px] max-w-full text-center relative animate-fadeIn">
            <div className="flex justify-center mb-4">
              {modal.action === "approve" ? (
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-100">
                  <FaCheck className="text-green-600 text-3xl" />
                </span>
              ) : modal.action === "reject" ? (
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-100">
                  <FaTimes className="text-red-600 text-3xl" />
                </span>
              ) : null}
            </div>
            <h2 className="text-xl font-bold mb-2">
              {modal.action === "approve"
                ? "Approve Advertisement"
                : modal.action === "reject"
                  ? "Reject Advertisement"
                  : ""}
            </h2>
            <p className="mb-6 text-gray-700">
              {modal.action === "approve"
                ? "Are you sure you want to approve this advertisement?"
                : modal.action === "reject"
                  ? "Are you sure you want to reject this advertisement?"
                  : ""}
            </p>
            {modal.action === "reject" && (
              <div className="mb-6 text-left">
                <label
                  htmlFor="rejectReason"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Reason for Rejection:
                </label>
                <textarea
                  id="rejectReason"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
                  rows={3}
                  placeholder="Enter reason for rejection..."
                />
              </div>
            )}
            <div className="flex justify-center gap-4">
              <button
                onClick={handleModalConfirm}
                className={`px-5 py-2 rounded-lg font-semibold shadow transition-colors text-white ${modal.action === "approve"
                  ? "bg-green-500 hover:bg-green-600"
                  : "bg-red-500 hover:bg-red-600"
                  }`}
              >
                Yes
              </button>
              <button
                onClick={handleModalCancel}
                className="px-5 py-2 rounded-lg font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 shadow transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer position="top-right" autoClose={1500} />
      {showApproveSpinner && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl border border-blue-200 animate-fadeIn">
            {/* Custom Tailwind Spinner */}
            <div className="relative mb-4 w-16 h-16 grid place-items-center">
              {/* Outer ring */}
              <span className="absolute inset-0 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin" style={{ borderWidth: "4px" }} />
              {/* Middle ring */}
              <span className="absolute inset-0 m-1 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slow" style={{ borderWidth: "4px", animationDuration: "2s" }} />
              {/* Inner ring */}
              <span className="absolute inset-0 m-2 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slower" style={{ borderWidth: "4px", animationDuration: "3s" }} />
            </div>
            <span className="text-lg font-semibold text-blue-700 tracking-wide">
              Processing Approval...
            </span>
            <span className="text-sm text-gray-500 mt-2">
              Please wait while we update the status.
            </span>
          </div>
        </div>
      )}
      {showRejectSpinner && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl border border-blue-200 animate-fadeIn">
            {/* Custom Tailwind Spinner */}
            <div className="relative mb-4 w-16 h-16 grid place-items-center">
              {/* Outer ring */}
              <span className="absolute inset-0 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin" style={{ borderWidth: "4px" }} />
              {/* Middle ring */}
              <span className="absolute inset-0 m-1 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slow" style={{ borderWidth: "4px", animationDuration: "2s" }} />
              {/* Inner ring */}
              <span className="absolute inset-0 m-2 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slower" style={{ borderWidth: "4px", animationDuration: "3s" }} />
            </div>
            <span className="text-lg font-semibold text-red-700 tracking-wide">
              Processing Rejection...
            </span>
            <span className="text-sm text-gray-500 mt-2">
              Please wait while we update the status.
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default PendingPost;


